// Public hook + helpers to surface admin-uploaded room galleries on the
// website. One fetch per hotel page (batch by-hotel endpoint) → map of
// room_slug → image[]. Falls back gracefully to the existing room.gallery
// or room.image when no admin gallery exists.

import { useEffect, useState } from "react";
import { slugifyRoom } from "@/lib/roomRegistry";

const API = process.env.REACT_APP_BACKEND_URL || "";

const resolveUrl = (url) => {
  if (!url) return null;
  if (typeof url !== "string") return null;
  if (url.startsWith("/api/")) return `${API}${url}`;
  return url;
};

// Module-level cache so navigating away and back doesn't refetch.
const cache = new Map(); // hotelSlug → { galleries }

export const useHotelGalleries = (hotelSlug) => {
  const [galleries, setGalleries] = useState(() => cache.get(hotelSlug)?.galleries || {});

  useEffect(() => {
    if (!hotelSlug) return undefined;
    let cancelled = false;
    fetch(`${API}/api/galleries/by-hotel/${hotelSlug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        const map = data.galleries || {};
        cache.set(hotelSlug, { galleries: map });
        setGalleries(map);
      })
      .catch(() => {
        // Silent — admin galleries are optional, fallbacks cover this.
      });
    return () => { cancelled = true; };
  }, [hotelSlug]);

  return galleries;
};

// Returns the merged image list for one room:
//   1. Admin-uploaded gallery (if any)            ← highest priority
//   2. Existing room.gallery (hard-coded data.js)
//   3. Fallback [room.image, …hotel.gallery, hotel.image]
export const getRoomImagesFromAdmin = (room, hotel, galleries) => {
  const adminEntry = galleries?.[slugifyRoom(room)];
  if (adminEntry && Array.isArray(adminEntry.images) && adminEntry.images.length) {
    return adminEntry.images.map((img) => resolveUrl(img.url)).filter(Boolean);
  }
  if (room.gallery && room.gallery.length) return room.gallery;
  const pool = [room.image, ...(hotel.gallery || []), hotel.image].filter(Boolean);
  return Array.from(new Set(pool));
};

// Quick check used by the inline room hero (slider vs single image)
export const hasAdminGallery = (room, galleries) => {
  const adminEntry = galleries?.[slugifyRoom(room)];
  return !!(adminEntry && adminEntry.images && adminEntry.images.length);
};
